import { useEffect, useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { searchTaskProducts } from "@/utils/api";
import { toProductSetProposal } from "@/utils/productSet";

type UseProductSetArgs = {
  currentTaskId: string | null;
  productSetId: string | null;
};

/**
 * Product-set search: lets the reviewer search the full product set for a track via the
 * server-side search endpoint. Debounced so we only hit the API once the user stops typing,
 * and gated so nothing is fetched until they actually type something.
 */
export const useProductSet = ({
  currentTaskId,
  productSetId,
}: UseProductSetArgs) => {
  const [query, setQuery] = useState("");

  // Reset the search whenever we move to a new task.
  useEffect(() => {
    setQuery("");
  }, [currentTaskId]);

  const [debouncedQuery] = useDebouncedValue(query, 300);
  const trimmed = debouncedQuery.trim();

  const searchQuery = useQuery({
    queryKey: ["task-products", currentTaskId, trimmed],
    queryFn: () => searchTaskProducts(currentTaskId ?? "", trimmed),
    enabled: !!currentTaskId && !!productSetId && trimmed.length > 0,
    select: (items) => items.map(toProductSetProposal),
  });

  // Show the loader while a search is in flight, including the debounce gap between the
  // latest keystroke and the request firing.
  const isSearching =
    query.trim().length > 0 &&
    (searchQuery.isFetching || query !== debouncedQuery);

  return {
    searchResults: searchQuery.data ?? [],
    /** Props for the PredictionGrid search toolbar. */
    controls: {
      hasProductSet: !!productSetId,
      query,
      onQueryChange: setQuery,
      isSearching,
      searchError: searchQuery.error?.message ?? null,
    },
  };
};
